import React, { useState } from "react";
import Layout from "../../components/layout/Layout";
import useRead from "../../hooks/useRead";
import useGet from "../../hooks/read/useGet";
import usePut from "../../hooks/usePut";
import { ChevronLeftIcon } from "@heroicons/react/solid";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Alert, Snackbar } from "@mui/material";
import spinner from "../../assets/images/spinner.gif";
import useDownloadFile from "../../hooks/useDownloadFile";
import { config } from "../../config";

import StatusBadge from "./components/StatusBadge";
import EmployeeDetailsCard from "./components/EmployeeDetailsCard";
import AttendanceSummaryTable from "./components/AttendanceSummaryTable";
import LeaveDetailsTables from "./components/LeaveDetailsTables";
// import SchemeSwitcher from "./components/SchemeSwitcher";
import NewSchemePayout from "./components/NewSchemePayout";
// import OldSchemePayout from "./components/OldSchemePayout";
import PayoutSummaryFooter from "./components/PayoutSummaryFooter";
import AttendanceCalendarModal from "./components/AttendanceCalendarModal";
import StatutoryBreakupModal from "./components/StatutoryBreakupModal";
import SalaryBreakupModal from "./components/SalaryBreakupModal";

const tempData = {
  employee_name: "",
  salary: "",
  gst: "",
  professional_tax: "",
  approved_leaves: "",
  tds: "",
  base_salary: "",
  total_days_present: "",
  total_days_absent: "",
  department: "",
  designation: "",
  absent_deduction: "",
  total_pending_dates: "",
  days_present: "",
  days_absent: "",
  public_holidays: "",
  approved_leaves_dates: "",
  month_days: "",
  pending_dates: "",
  prev_month_sal_deduction: "",
  previous_month_absent: "",
  previous_month_days: "",
};

const getDateMonthString = (date) => {
  let tempDate = new Date(date);
  let month = tempDate.getMonth() + 1;
  return tempDate.getFullYear() + "-" + month;
};

const SalaryLogs = () => {
  const navigate = useNavigate();
  // setSearchParams was only needed by the scheme switcher
  // let [searchParams, setSearchParams] = useSearchParams();
  let [searchParams] = useSearchParams();
  const [currentDatesSection, setCurrentDatesSection] = useState("");
  const [listData, setListData] = useState(tempData);
  const [initialListData, setInitialListData] = useState(null);
  // New Scheme is the only active scheme.
  // const [selectedScheme, setSelectedScheme] = useState(
  //   searchParams.get("scheme") || "old"
  // );
  const [selectedScheme] = useState("new");
  const [filterData, setFilterData] = useState({
    month_and_year: new Date(searchParams.get("date")),
    prev_month_and_year: new Date(searchParams.get("prev_date")),
  });

  // Fetch salary structures list without show_status filtering
  const { list: structureListReq } = useGet({
    url: "salary-structure/list",
    initialData: {
      page_number: 0,
      limit: 50,
      search_query: "",
    },
  });

  const structureList = structureListReq?.data?.data || [];

  // State for amount and remarks
  const [earningsAmount, setEarningsAmount] = useState("");
  const [earningsRemarks, setEarningsRemarks] = useState("");
  const [deductionAmount, setDeductionAmount] = useState("");
  const [deductionRemarks, setDeductionRemarks] = useState("");

  // Popup messages
  const [createSuccessMessage, setCreateSuccessMessage] = useState(false);
  const [updateSuccessMessage, setUpdateSuccessMessage] = useState(false);
  const [errorWhileGettingData, setErrorWhileGettingError] = useState(false);

  // setGetListParams was only needed by the scheme switcher
  // const { list, setGetListParams } = useRead({
  const { list } = useRead({
    url: "getUserPayout",
    initialData: {
      month_year: filterData.month_and_year,
      user_id: searchParams.get("id"),
      scheme_type: selectedScheme,
    },
    onSuccess: (data) => {
      setInitialListData(data?.data[0] || data?.data);
      setListData(data?.data);

      // Pre-fill value and remarks
      const payoutData = data?.data?.salary_payout || {};
      setEarningsAmount(payoutData.earnings_amount || "");
      setEarningsRemarks(payoutData.earnings_remarks || "");
      setDeductionAmount(payoutData.deduction_amount || "");
      setDeductionRemarks(payoutData.deduction_remarks || "");

      // Old logic that switched the page back to the scheme stored on the payout.
      // if (payoutData.scheme_type && !searchParams.get("scheme")) {
      //   setSelectedScheme(payoutData.scheme_type);
      // }
    },
    onError: () => {
      setErrorWhileGettingError(true);
    },
  });

  // Scheme switching disabled - New Scheme is the only active scheme.
  // const handleSchemeChange = (slug) => {
  //   setSelectedScheme(slug);
  //   const nextParams = new URLSearchParams(searchParams);
  //   nextParams.set("scheme", slug);
  //   setSearchParams(nextParams, { replace: true });
  //   setGetListParams((prev) => ({
  //     ...prev,
  //     scheme_type: slug,
  //   }));
  // };

  const [showDates, setShowDates] = useState(false);
  const [showStatutoryModal, setShowStatutoryModal] = useState(false);
  const [showSalaryBreakupModal, setShowSalaryBreakupModal] = useState(false);

  const handleViewDates = (datesSection) => {
    getCalendar.handleAuth.mutate({
      user_id: searchParams.get("id"),
      month_year: getDateMonthString(searchParams.get("date")),
    });
    setShowDates(true);
    setCurrentDatesSection(datesSection);
  };

  const getCalendar = usePut({
    url: "getCalenderDetails",
    onSuccess: (data) => {
      let resData = data?.data?.data[0];
      setListData({
        ...listData,
        ...resData,
      });
    },
    onError: () => {},
    onSettled: () => {},
  });

  const handleSetTDS = (e) => {
    let copyObject = { ...listData };
    if (!copyObject.salary_payout) {
      copyObject.salary_payout = {};
    }
    copyObject.salary_payout.tds = +e.target.value;
    setListData(copyObject);
  };

  const storeTdsInPayout = usePut({
    url: "storeTdsInPayout",
    onSuccess: () => {
      setUpdateSuccessMessage(true);
    },
  });

  const handleStoreTdsInPayout = () => {
    let data = {
      payout_id: listData?.salary_payout?.payout_id,
      scheme_type: selectedScheme,
      tds: listData?.salary_payout?.tds || 0,
      earnings_amount: earningsAmount,
      deduction_amount: deductionAmount,
      earnings_remarks: earningsRemarks,
      deduction_remarks: deductionRemarks,
      net_payable: listData?.salary_payout?.net_payable,
    };
    storeTdsInPayout.handleAuth.mutate(data);
  };

  const { reportDownload } = useDownloadFile({
    url: "salary_slip",
    filename: "salary_slip.pdf",
  });

  const isReadOnly =
    listData?.salary_payout?.processing_status === "1" ||
    listData?.salary_payout?.processing_status === "2";

  // Distinguish initial load from background scheme refetch
  const hasLoadedInitialData = Boolean(listData?.employee_details?.employee_name);
  const isInitialLoading = list?.isLoading && !hasLoadedInitialData;
  const isSchemeFetching = list?.isFetching && hasLoadedInitialData;

  return (
    <Layout>
      <div className="mx-6 my-4 flex justify-between items-center">
        <button
          onClick={() => navigate(-1, { replace: true })}
          className="flex items-center hover:underline"
        >
          <ChevronLeftIcon className="w-6 h-6" />
          <span className="font-semibold">Back</span>
        </button>
        <a
          download
          href={`${config.apiurl}salary_slip?month_year=${
            filterData.month_and_year
          }&user_id=${searchParams.get("id")}&scheme_type=${selectedScheme}`}
          className="text-blue-500 font-semibold hover:underline"
        >
          Download Salary Slip
        </a>
      </div>

      {isInitialLoading || reportDownload.isLoading ? (
        <div className="w-16 h-16 mx-auto mt-8">
          <img src={spinner} alt="spinner" className="w-full h-full" />
        </div>
      ) : (
        <div className="mx-6 mb-10">
          <div>
            <StatusBadge status={listData?.salary_payout?.processing_status} />
          </div>

          <div className="document-body py-4 overflow-auto">
            {/* Employee Details Tables (Constant between schemes) */}
            <EmployeeDetailsCard
              employeeDetails={listData?.employee_details}
              payout={listData?.salary_payout}
              leaveBalance={listData?.leave_balance_data}
            />

            {/* Attendance Details Table (Constant between schemes) */}
            <AttendanceSummaryTable
              payout={listData?.salary_payout}
              onViewDates={handleViewDates}
            />

            {/* Basic Leaves Detail Table + Conditional OT Tables (Constant between schemes) */}
            <LeaveDetailsTables
              leaveBalance={listData?.leave_balance_data}
            />

            {/* Scheme Toggle Buttons & Breakup Actions */}
            <div className="flex flex-wrap justify-between items-center my-4 gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowStatutoryModal(true)}
                  disabled={isSchemeFetching}
                  className="px-5 py-2 rounded-md font-semibold text-sm transition-all border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  View Statutory Break Up
                </button>
                {(Number(listData?.salary_payout?.sub_record_count) > 1 ||
                  Number(listData?.sub_record_count) > 1 ||
                  (Array.isArray(
                    listData?.salary_payout?.salary_based_payout_data
                  ) &&
                    listData.salary_payout.salary_based_payout_data.length >
                      1)) && (
                  <button
                    type="button"
                    onClick={() => setShowSalaryBreakupModal(true)}
                    disabled={isSchemeFetching}
                    className="px-5 py-2 rounded-md font-semibold text-sm transition-all border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    View Salary Break Up
                  </button>
                )}
              </div>
              {/* <div className="-my-4">
                <SchemeSwitcher
                  structureList={structureList}
                  selectedScheme={selectedScheme}
                  handleSchemeChange={handleSchemeChange}
                  isLoading={isSchemeFetching}
                />
              </div> */}
            </div>

            {/* Scheme Payout Details (Reloads on scheme switch) */}
            {isSchemeFetching ? (
              <div className="py-16 my-8 bg-gray-50 border border-gray-200 rounded-lg flex flex-col items-center justify-center">
                <img src={spinner} alt="Loading scheme data..." className="w-10 h-10" />
                <p className="mt-3 text-sm font-semibold text-gray-600">
                  Loading {selectedScheme.toLowerCase() === "new" ? "New" : "Old"} Scheme Calculations...
                </p>
              </div>
            ) : (
              <>
                {/* New Scheme is the only active scheme - payout is rendered directly */}
                <NewSchemePayout
                  listData={listData}
                  earningsAmount={earningsAmount}
                  setEarningsAmount={setEarningsAmount}
                  earningsRemarks={earningsRemarks}
                  setEarningsRemarks={setEarningsRemarks}
                  deductionAmount={deductionAmount}
                  setDeductionAmount={setDeductionAmount}
                  deductionRemarks={deductionRemarks}
                  setDeductionRemarks={setDeductionRemarks}
                  handleSetTDS={handleSetTDS}
                  isReadOnly={isReadOnly}
                />
                {/* Old scheme payout disabled - restored by switching back to the ternary above
                <OldSchemePayout
                  listData={listData}
                  earningsAmount={earningsAmount}
                  setEarningsAmount={setEarningsAmount}
                  earningsRemarks={earningsRemarks}
                  setEarningsRemarks={setEarningsRemarks}
                  deductionAmount={deductionAmount}
                  setDeductionAmount={setDeductionAmount}
                  deductionRemarks={deductionRemarks}
                  setDeductionRemarks={setDeductionRemarks}
                  handleSetTDS={handleSetTDS}
                  isReadOnly={isReadOnly}
                />
                */}

                {/* Footer OT, Late & Net Summary + Save Action */}
                <PayoutSummaryFooter
                  payout={listData?.salary_payout}
                  onSave={handleStoreTdsInPayout}
                  isSaving={storeTdsInPayout.handleAuth.isLoading}
                />
              </>
            )}
          </div>
        </div>
      )}

      {/* Attendance Calendar Modal */}
      <AttendanceCalendarModal
        showDates={showDates}
        setShowDates={setShowDates}
        isLoading={getCalendar.handleAuth.isLoading}
        currentDatesSection={currentDatesSection}
        attendanceDates={{
          days_present: listData?.salary_payout?.present_dates,
          days_absent: listData?.salary_payout?.absent_dates,
          approved_leaves_dates: listData?.salary_payout?.leave_dates,
          pending_dates: listData?.salary_payout?.pending_dates,
          month_days: listData?.salary_payout?.month_dates,
          public_holidays: listData?.salary_payout?.holiday_dates,
        }}
        monthDateObject={filterData?.month_and_year}
      />

      {/* Statutory Breakup Modal */}
      <StatutoryBreakupModal
        isOpen={showStatutoryModal}
        onClose={() => setShowStatutoryModal(false)}
        statutoryBreakup={listData?.salary_payout?.statutory_breakup}
        employeeName={listData?.employee_details?.employee_name}
        monthYear={listData?.salary_payout?.month_year}
      />

      {/* Salary Breakup Modal */}
      <SalaryBreakupModal
        isOpen={showSalaryBreakupModal}
        onClose={() => setShowSalaryBreakupModal(false)}
        breakupData={listData?.salary_payout?.salary_based_payout_data}
        employeeName={listData?.employee_details?.employee_name}
        monthYear={listData?.salary_payout?.month_year}
      />

      <Snackbar
        open={createSuccessMessage}
        autoHideDuration={6000}
        onClose={() => setCreateSuccessMessage(false)}
      >
        <Alert severity="success" sx={{ width: "100%" }}>
          Payment Created Successfully
        </Alert>
      </Snackbar>
      <Snackbar
        open={updateSuccessMessage}
        autoHideDuration={6000}
        onClose={() => setUpdateSuccessMessage(false)}
      >
        <Alert severity="success" sx={{ width: "100%" }}>
          Payment Updated Successfully
        </Alert>
      </Snackbar>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={errorWhileGettingData}
        autoHideDuration={6000}
        onClose={() => setErrorWhileGettingError(false)}
      >
        <Alert
          onClose={() => setErrorWhileGettingError(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          There was an error while retrieving the data; please try again later.
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default SalaryLogs;